import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Box, Stack } from "@mui/system";

import { Link } from "react-router-dom";

import { FileDropzone, Modal } from "@/components";
import { useUploadMutation } from "@/pages/Private/redux/account/account.api";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { LinkedinConnections, LinkedinConnectionsSchema } from "../../schema/account";

interface UploadConnectionsModalProps {
	isVisible: boolean;
	handleClose: () => void;
}

export const UploadConnectionsModal: FunctionComponent<UploadConnectionsModalProps> = ({
	isVisible,
	handleClose,
}) => {
	const { t } = useTranslation();

	const [currentFile, setCurrentFile] = useState<File | null>(null);

	const [upload, { isLoading, error }] = useUploadMutation();
	const { run: runUpload, isRunning } = useSingleFlight();

	const {
		setValue,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<LinkedinConnections>({
		resolver: zodResolver(LinkedinConnectionsSchema),
	});

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const submit = async (values: LinkedinConnections) => {
		try {
			if (!values.file) {
				return;
			}

			try {
				const formData = new FormData();
				const res: Response = await fetch(URL.createObjectURL(values.file));
				const blob: Blob = await res.blob();
				const file = new File([blob], "file.csv");

				formData.append("file", file);
				formData.append("type", "csv");
				await upload(formData).unwrap();
			} catch (err) {
				console.error(err);
			} finally {
				reset();
				handleClose();
			}
		} catch (err) {
			console.error(err);
		}
	};

	// The picked file is fetched and read into a Blob before the mutation starts, so the
	// mutation's isLoading could not close the window between two clicks (PREC-4554).
	const onSubmit = (values: LinkedinConnections) => runUpload(() => submit(values));

	const handleDrop = async (files: File[]): Promise<void> => {
		if (files.length) {
			setCurrentFile(files[0]);
			setValue("file", files[0]);
		}
	};

	const handleCloseModal = () => {
		setCurrentFile(null);
		reset();
		handleClose();
	};

	return (
		<Modal
			disableSave={!currentFile}
			handleClose={handleCloseModal}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading || isRunning}
			isOpened={isVisible}
			submitButtonText={t("basics.upload")}
			title={"Upload your linkedin connections"}
		>
			<Box component="form">
				<Stack mt="2" spacing={2}>
					<FileDropzone
						accept={{ "text/*": [".csv"] }}
						caption={
							currentFile
								? currentFile.name
								: "Please upload the CSV file you downloaded from LinkedIn"
						}
						maxFiles={1}
						onDrop={handleDrop}
					/>

					<div className="text-text-meta text-sm">
						Please follow these{" "}
						<Link
							className="text-link underline"
							target="_blank"
							to={
								"https://www.linkedin.com/help/linkedin/answer/a566336/export-connections-from-linkedin"
							}
						>
							instructions
						</Link>{" "}
						to download your connections.
					</div>

					{getAllErrors(error, formErrors).length
						? renderErrorMessages(getAllErrors(error, formErrors))
						: null}
				</Stack>
			</Box>
		</Modal>
	);
};
